import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";

export default function ContactSection({ compact = false, intro }) {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "", expertise: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you. We will be in touch shortly.");
      setForm({ name: "", email: "", company: "", subject: "", message: "", expertise: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className={`${compact ? "py-20 md:py-28" : "py-28 md:py-40"} reveal hairline-top`}
      ref={ref}
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <h2 className="h-display text-[clamp(2.2rem,5vw,4.4rem)]">
              Start a<br />conversation.
            </h2>
            {intro && (
              <p className="mt-6 text-graphite/80 leading-relaxed max-w-md" data-testid="contact-intro">
                {intro}
              </p>
            )}
            <div className="mt-10 space-y-8 text-graphite/85 leading-relaxed">
              <div>
                <p className="text-xs text-graphite/50 mb-2">Office</p>
                <p className="leading-relaxed">1 Quality Court<br />Chancery Lane<br />London WC2A 1HR</p>
              </div>
              <div>
                <p className="text-xs text-graphite/50 mb-2">Telephone</p>
                <p><a href="tel:+442031051755" className="hover:text-[#1E3668]">+44 (0) 203 105 1755</a></p>
              </div>
              <div>
                <p className="text-xs text-graphite/50 mb-2">Email</p>
                <p><a href="mailto:info@ceclegal.co.uk" className="hover:text-[#1E3668]">info@ceclegal.co.uk</a></p>
              </div>
            </div>
          </div>

          <form className="md:col-span-7" onSubmit={submit} data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div>
                <label className="text-xs text-graphite/55 block mb-1">Name</label>
                <input data-testid="contact-name" className="edit-input" required value={form.name} onChange={update("name")} />
              </div>
              <div>
                <label className="text-xs text-graphite/55 block mb-1">Email</label>
                <input data-testid="contact-email" type="email" className="edit-input" required value={form.email} onChange={update("email")} />
              </div>
              <div>
                <label className="text-xs text-graphite/55 block mb-1">Company</label>
                <input data-testid="contact-company" className="edit-input" value={form.company} onChange={update("company")} />
              </div>
              <div>
                <label className="text-xs text-graphite/55 block mb-1">Practice area</label>
                <input data-testid="contact-expertise" className="edit-input" placeholder="Optional" value={form.expertise} onChange={update("expertise")} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-graphite/55 block mb-1">Subject</label>
                <input data-testid="contact-subject" className="edit-input" value={form.subject} onChange={update("subject")} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-graphite/55 block mb-1">Message</label>
                <textarea data-testid="contact-message" required className="edit-textarea" rows={5} value={form.message} onChange={update("message")} />
              </div>
            </div>
            <div className="mt-10">
              <button type="submit" disabled={sending} className="cec-btn cec-btn-primary" data-testid="contact-submit">
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </div>
            <p className="mt-6 text-xs text-graphite/55 max-w-prose">
              By submitting this form you agree to be contacted by Carlton Elleker Chase about your enquiry.
              We will never share your details with third parties.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
